import { z } from 'zod';
import { SeedType } from './seeds';
import { ScanStatus } from './transitions';

/**
 * Contrats des scans (`api.md` § Scans, `data-model.md` § `scans`). Les colonnes `jsonb` stockées
 * (`config`, `counts`) sont exposées telles quelles, en `snake_case` ; le reste suit la convention
 * `camelCase` de l'API.
 */

// Helpers locaux (T06 les factorisera dans `common.ts`).
const Id = z.string().min(1);
const IsoDate = z.string();

/** Statuts pendant lesquels un autre scan ne peut pas être créé (`409 scan_already_running`). */
export const SCAN_ACTIVE_STATUSES = ['queued', 'running', 'paused_budget'] as const;

export const ScanPhase = z.enum([
  'ingest',
  'variants',
  'embed',
  'cluster',
  'metrics',
  'prefilter',
  'triage',
  'serp',
  'supply',
  'deep',
  'score',
  'finalize',
]);
export type ScanPhase = z.infer<typeof ScanPhase>;
export const SCAN_PHASES = ScanPhase.options;

export const ScanSeedStatus = z.enum(['pending', 'done', 'failed', 'skipped']);
export type ScanSeedStatus = z.infer<typeof ScanSeedStatus>;

export const EventLevel = z.enum(['info', 'warn', 'error']);
export type EventLevel = z.infer<typeof EventLevel>;

/** Plafonds d'un scan tels que saisis (`camelCase`) ; bornes de la fiche T10. */
export const ScanCapsInput = z.object({
  triage: z.number().int().min(0).max(10_000).optional(),
  serpClusters: z.number().int().min(0).max(5_000).optional(),
  deep: z.number().int().min(0).max(1_000).optional(),
  costUsd: z.number().min(0).max(500).optional(),
});
export type ScanCapsInput = z.infer<typeof ScanCapsInput>;

const seedIdsField = z
  .array(Id)
  .min(1)
  .max(200)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'validation.uniqueSeedIds' });

export const CreateScanBody = z.object({
  marketId: Id,
  label: z.string().trim().min(1).max(120),
  seedIds: seedIdsField,
  caps: ScanCapsInput.optional(),
  thetaAssign: z.number().min(0.5).max(0.99).optional(),
  serpPerCluster: z.number().int().min(1).max(3).optional(),
});
export type CreateScanBody = z.infer<typeof CreateScanBody>;

export const CreateScanResponse = z.object({ id: Id });
export type CreateScanResponse = z.infer<typeof CreateScanResponse>;

/** `scans.config` tel que stocké (`pipeline.md` § Paramètres d'un scan). */
export const ScanConfig = z.object({
  seed_ids: z.array(z.string()),
  caps: z.object({
    triage: z.number(),
    serp_clusters: z.number(),
    deep: z.number(),
    cost_usd: z.number(),
  }),
  theta_assign: z.number(),
  serp_per_cluster: z.number(),
  serp_max_age_days: z.number(),
  scoring_config_version: z.number(),
  options: z.object({
    include_adult_keywords: z.boolean(),
    page_size: z.number(),
    retries: z.number().optional(),
  }),
});
export type ScanConfig = z.infer<typeof ScanConfig>;

export const ScanCounts = z
  .object({
    ideas_fetched: z.number(),
    keywords_new: z.number(),
    keywords_updated: z.number(),
    variants_grouped: z.number(),
    embedded: z.number(),
    clusters_new: z.number(),
    clusters_updated: z.number(),
    candidates: z.number(),
    triaged: z.number(),
    serps_requested: z.number(),
    serps_ready: z.number(),
    deep_analyses: z.number(),
    opportunities_new: z.number(),
    opportunities_rescored: z.number(),
  })
  .partial();
export type ScanCounts = z.infer<typeof ScanCounts>;

export const ScanRow = z.object({
  id: Id,
  marketId: z.string(),
  label: z.string(),
  status: ScanStatus,
  phase: ScanPhase.nullable(),
  seedsCount: z.number(),
  counts: ScanCounts,
  costUsd: z.number(),
  startedAt: IsoDate.nullable(),
  finishedAt: IsoDate.nullable(),
  createdAt: IsoDate,
  createdBy: z.object({ id: z.string(), name: z.string() }),
});
export type ScanRow = z.infer<typeof ScanRow>;

export const ScanSeedRow = z.object({
  seedId: Id,
  label: z.string(),
  status: ScanSeedStatus,
  ideasCount: z.number(),
  pages: z.number(),
  error: z.string().nullable(),
});
export type ScanSeedRow = z.infer<typeof ScanSeedRow>;

/** Statut de l'instance Workflow (`null` si l'instance est introuvable). */
export const WorkflowStatus = z.object({ status: z.string(), error: z.string().nullable() });
export type WorkflowStatus = z.infer<typeof WorkflowStatus>;

export const ScanDetail = ScanRow.extend({
  config: ScanConfig,
  estimatedCostUsd: z.number().nullable(),
  error: z.string().nullable(),
  seeds: z.array(ScanSeedRow),
  workflowStatus: WorkflowStatus.nullable(),
});
export type ScanDetail = z.infer<typeof ScanDetail>;

export const ScansQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.enum(['createdAt']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  marketId: z.string().optional(),
  /** Un statut ou plusieurs (`?status=running&status=queued`). */
  status: z
    .union([ScanStatus, z.array(ScanStatus)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional(),
});
export type ScansQuery = z.infer<typeof ScansQuery>;

export const ScansResponse = z.object({
  items: z.array(ScanRow),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});
export type ScansResponse = z.infer<typeof ScansResponse>;

export const ScanEventRow = z.object({
  id: z.number(),
  phase: ScanPhase.nullable(),
  level: EventLevel,
  message: z.string(),
  data: z.record(z.string(), z.unknown()).nullable(),
  createdAt: IsoDate,
});
export type ScanEventRow = z.infer<typeof ScanEventRow>;
/** Alias de la fiche T11 (`ScanEvent`). */
export const ScanEvent = ScanEventRow;
export type ScanEvent = ScanEventRow;

/** `GET /scans/:id/events?afterId=&pageSize=` : ordre croissant par `id`, polling incrémental. */
export const ScanEventsQuery = z.object({
  afterId: z.coerce.number().int().min(0).default(0),
  pageSize: z.coerce.number().int().min(1).max(500).default(200),
});
export type ScanEventsQuery = z.infer<typeof ScanEventsQuery>;

export const ScanEventsResponse = z.object({
  items: z.array(ScanEventRow),
  /** Dernier `id` renvoyé (ou `afterId` si la page est vide) : à passer au prochain appel. */
  lastId: z.number(),
  hasMore: z.boolean(),
});
export type ScanEventsResponse = z.infer<typeof ScanEventsResponse>;

export const EstimateScanBody = z.object({
  marketId: Id,
  seedIds: seedIdsField,
  caps: ScanCapsInput.optional(),
  serpPerCluster: z.number().int().min(1).max(3).optional(),
});
export type EstimateScanBody = z.infer<typeof EstimateScanBody>;

export const EstimatePhase = z.enum(['ingest', 'embed', 'triage', 'serp', 'deep']);
export type EstimatePhase = z.infer<typeof EstimatePhase>;

export const ScanEstimateLine = z.object({
  phase: EstimatePhase,
  label: z.string(),
  units: z.string(),
  costUsd: z.number(),
});
export type ScanEstimateLine = z.infer<typeof ScanEstimateLine>;

/** `POST /scans/estimate` : par phase, total, et position du garde-fou budget. */
export const ScanEstimate = z.object({
  phases: z.object({
    ingest: z.number(),
    embed: z.number(),
    triage: z.number(),
    serp: z.number(),
    deep: z.number(),
  }),
  lines: z.array(ScanEstimateLine),
  totalUsd: z.number(),
  scanCapUsd: z.number(),
  monthlyCapUsd: z.number(),
  monthlySpentUsd: z.number(),
  monthlyRemainingUsd: z.number(),
  /** Première phase payante dont le cumul dépasserait un plafond, `null` sinon. */
  pauseAtPhase: EstimatePhase.nullable(),
  assumptions: z.record(z.string(), z.unknown()),
});
export type ScanEstimate = z.infer<typeof ScanEstimate>;
/** Alias de la fiche T11 (`EstimateScanResponse`). */
export const EstimateScanResponse = ScanEstimate;
export type EstimateScanResponse = ScanEstimate;

export const ScanResultOpportunity = z.object({
  id: Id,
  title: z.string(),
  attractiveness: z.number().nullable(),
  confidence: z.number().nullable(),
});
export const ScanResultRescored = z.object({
  id: Id,
  title: z.string(),
  before: z.number().nullable(),
  after: z.number().nullable(),
});
export const ScanResultMergeSuggestion = z.object({
  id: Id,
  clusterAId: Id,
  clusterBId: Id,
  clusterALabel: z.string(),
  clusterBLabel: z.string(),
  overlap: z.number(),
});

/**
 * `GET /scans/:id/results` : opportunités créées et rescorées par ce scan (événements `created` /
 * `rescored` portant `data.scan_id`) et suggestions de fusion `open` du scan.
 */
export const ScanResults = z.object({
  opportunitiesNew: z.array(ScanResultOpportunity),
  opportunitiesRescored: z.array(ScanResultRescored),
  mergeSuggestions: z.array(ScanResultMergeSuggestion),
});
export type ScanResults = z.infer<typeof ScanResults>;

/** Règles des portes G1–G7 (`scoring.md` § 2), telles qu'écrites dans `eligibility_reasons[].rule`. */
export const SCAN_GATE_RULES = [
  'exclusion',
  'low_demand',
  'informational',
  'news_local',
  'untestable',
  'triage',
  'brand',
] as const;
export const ScanGateRule = z.enum(SCAN_GATE_RULES);
export type ScanGateRule = z.infer<typeof ScanGateRule>;

/** Clusters touchés par le scan écartés par une porte (un cluster compte une fois par règle). */
export const ScanFunnelGateDrop = z.object({ rule: ScanGateRule, count: z.number().int() });
export type ScanFunnelGateDrop = z.infer<typeof ScanFunnelGateDrop>;

/** Motif compté : `triage:not_a_job`, `triage:exclusion:<catégorie>`, `below_threshold`… */
export const ScanFunnelReason = z.object({ code: z.string(), count: z.number().int() });
export type ScanFunnelReason = z.infer<typeof ScanFunnelReason>;

/**
 * Indice d'une seed terminée avec moins de `SCAN_FUNNEL_FEW_IDEAS` idées (T35, T37) :
 * `few_ideas_site` pour une seed `url` / `site` ; `few_ideas_long_keywords` quand la moyenne de mots
 * des mots-clés dépasse `SCAN_FUNNEL_LONG_KEYWORD_WORDS` (des phrases, Keyword Planner ne dérive
 * rien) ; `few_ideas` sinon (sujet étroit).
 */
export const ScanFunnelSeedHint = z.enum([
  'few_ideas_site',
  'few_ideas_long_keywords',
  'few_ideas',
]);
export type ScanFunnelSeedHint = z.infer<typeof ScanFunnelSeedHint>;
export const SCAN_FUNNEL_FEW_IDEAS = 50;
export const SCAN_FUNNEL_LONG_KEYWORD_WORDS = 4;

export const ScanFunnelSeed = z.object({
  id: Id,
  label: z.string(),
  type: SeedType,
  status: ScanSeedStatus,
  ideasFetched: z.number().int(),
  /** Depuis l'événement `ingest.seed_done` ; `null` si la seed n'a pas abouti. */
  keywordsNew: z.number().int().nullable(),
  /** Moyenne de mots des mots-clés de la seed (au dixième) ; `null` sans mot-clé. */
  avgKeywordWords: z.number().nullable(),
  hint: ScanFunnelSeedHint.nullable(),
});
export type ScanFunnelSeed = z.infer<typeof ScanFunnelSeed>;

export const ScanFunnelTriage = z.object({
  /** Sélections `scan_selections(triage)` du scan. */
  selected: z.number().int(),
  /** Sélections traitées avec une sortie valide. */
  triaged: z.number().int(),
  /** Triés, restés `candidate` et `triage_score ≥ minScore` : éligibles aux SERPs. */
  passed: z.number().int(),
  /** Triés avec `triage_score < minScore`. */
  belowThreshold: z.number().int(),
  failed: z.number().int(),
  invalid: z.number().int(),
  minScore: z.number(),
  /** Motifs G6/G7 des clusters triés (`triage:not_a_job`…), du plus fréquent au moins fréquent. */
  reasons: z.array(ScanFunnelReason),
});
export type ScanFunnelTriage = z.infer<typeof ScanFunnelTriage>;

export const ScanFunnelSerps = z.object({
  /** Clusters sélectionnés pour l'achat de SERPs (`scan_selections(serp)`). */
  clusters: z.number().int(),
  requested: z.number().int(),
  ready: z.number().int(),
  failed: z.number().int(),
});
export type ScanFunnelSerps = z.infer<typeof ScanFunnelSerps>;

export const ScanFunnelDeep = z.object({
  selected: z.number().int(),
  /** Analyses profondes `ok` du scan. */
  analyses: z.number().int(),
  invalid: z.number().int(),
  failed: z.number().int(),
});
export type ScanFunnelDeep = z.infer<typeof ScanFunnelDeep>;

export const ScanFunnelScoring = z.object({
  selected: z.number().int(),
  created: z.number().int(),
  rescored: z.number().int(),
  skipped: z.number().int(),
  /** `thresholds.opportunity_min_score` de la configuration du scan. */
  minScore: z.number(),
  /** Motifs des clusters ignorés (`below_threshold`, `no_analysis`…). */
  reasons: z.array(ScanFunnelReason),
});
export type ScanFunnelScoring = z.infer<typeof ScanFunnelScoring>;

/**
 * `GET /scans/:id/funnel` (T35) : l'entonnoir du scan avec ses motifs de chute, pour que la page
 * du scan explique un résultat (0 opportunité comme 86) sans relire le journal.
 */
export const ScanFunnel = z.object({
  ideasFetched: z.number().int(),
  keywordsNew: z.number().int(),
  keywordsUpdated: z.number().int(),
  /** Clusters `updated_scan_id = :id`. */
  clustersTouched: z.number().int(),
  /** `counts.candidates` : clusters touchés restés `candidate` à la fin du préfiltre. */
  candidates: z.number().int(),
  gateDrops: z.array(ScanFunnelGateDrop),
  triage: ScanFunnelTriage,
  serps: ScanFunnelSerps,
  deep: ScanFunnelDeep,
  scoring: ScanFunnelScoring,
  seeds: z.array(ScanFunnelSeed),
});
export type ScanFunnel = z.infer<typeof ScanFunnel>;

export const ScanActionResponse = z.object({ id: Id, status: ScanStatus });
export type ScanActionResponse = z.infer<typeof ScanActionResponse>;

export const RetryScanResponse = z.object({ id: Id, instanceId: z.string() });
export type RetryScanResponse = z.infer<typeof RetryScanResponse>;
